import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, MessageCircle, MapPin, ShieldCheck, AlertCircle, Package, Boxes, X, QrCode } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useApiQuery, useQueryClient } from "../lib/useApiQuery";
import { Loader, EmptyState } from "../components/Loader";
import { OtpBadge } from "../components/OtpBadge";
import { QrScanModal } from "../components/QrScanModal";
import DriverDashboard from "./driver/DriverDashboard";

const STATUS = {
  pending: { ar: "بانتظار التأكيد", c: "#FFB627" },
  confirmed: { ar: "تم التأكيد", c: "#3F8CFF" },
  rejected: { ar: "مرفوض", c: "#FF3D5A" },
  shipped: { ar: "تم الشحن", c: "#3742FA" },
  delivered: { ar: "تم التسليم", c: "#9333EA" },
  completed: { ar: "مكتمل", c: "#00D26A" },
  disputed: { ar: "قيد النزاع", c: "#FF3D5A" },
};

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState(null);
  const [deliverOrder, setDeliverOrder] = useState(null);
  const [otpInput, setOtpInput] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const isSeller = user?.role === "seller";
  const { data: orders = [], isLoading } = useApiQuery(["orders", "mine"], "/orders/mine", { staleTime: 15000 });
  const reload = () => qc.invalidateQueries({ queryKey: ["orders"] });

  if (user?.role === "driver") return <DriverDashboard />;

  const act = async (id, fn) => {
    if (busyId) return;
    setBusyId(id);
    try { await fn(); await reload(); } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };
  // Optimistically patch an order's status in the cache for an instant UI response.
  const patchStatus = (id, status) => {
    qc.setQueryData(["orders", "mine"], (prev) =>
      Array.isArray(prev) ? prev.map((o) => (o.order_id === id ? { ...o, status } : o)) : prev);
  };
  const actStatus = async (id, status, fn) => {
    if (busyId) return;
    const snapshot = qc.getQueryData(["orders", "mine"]);
    patchStatus(id, status);
    setBusyId(id);
    try { await fn(); await reload(); }
    catch (e) { qc.setQueryData(["orders", "mine"], snapshot); toast.error(e.message); }
    finally { setBusyId(null); }
  };
  const sellerLegacy = (id, status) => actStatus(id, status, () => api(`/orders/${id}/status`, { method: "PUT", body: JSON.stringify({ status }) }));
  const markShipped = (id) => actStatus(id, "shipped", () => api(`/orders/${id}/mark-shipped`, { method: "POST", body: "{}" }));
  const markDelivered = (id) => actStatus(id, "delivered", () => api(`/orders/${id}/mark-delivered`, { method: "POST", body: "{}" }));
  const isSelfDelivery = (it) => !!it.self_delivery || !it.assigned_driver_id;
  const openSelfDeliver = async (order) => {
    if (busyId) return;
    setBusyId(order.order_id);
    try {
      await api(`/orders/${order.order_id}/seller-request-delivery-otp`, { method: "POST", body: "{}" });
      setDeliverOrder(order); setOtpInput(""); await reload();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };
  const submitSellerDeliver = async () => {
    if (!deliverOrder) return;
    const code = otpInput.trim();
    if (code.length < 4) { toast.error("أدخل رمز الاستلام (4 أرقام)"); return; }
    setOtpBusy(true);
    try {
      await api(`/orders/${deliverOrder.order_id}/seller-deliver`, { method: "POST", body: JSON.stringify({ otp: code }) });
      setDeliverOrder(null); setOtpInput(""); toast.success("تم تأكيد التسليم"); await reload();
    } catch (e) { toast.error(e.message); } finally { setOtpBusy(false); }
  };
  const onQrScan = (data) => {
    const digits = (data || "").replace(/\D/g, "").slice(0, 4);
    if (digits.length === 4) { setOtpInput(digits); setScanOpen(false); }
  };
  const confirmReceived = (id) => { if (window.confirm("بتأكيدك سيتم تحرير المبلغ للبائع فوراً. متأكد؟")) act(id, () => api(`/orders/${id}/confirm-received`, { method: "POST", body: "{}" })); };
  const openDispute = async (order) => {
    const reason = window.prompt("سبب النزاع؟ (مثلاً: لم يصل المنتج / المنتج تالف)");
    if (!reason) return;
    act(order.order_id, () => api(`/orders/${order.order_id}/dispute`, { method: "POST", body: JSON.stringify({ reason, media_base64: [], media_types: [] }) }).then(() => toast.success("تم فتح النزاع")));
  };

  if (isLoading) return <Loader />;

  return (
    <div className="animate-fade-in-up max-w-3xl mx-auto">
      <h1 className="text-2xl font-black text-[#161629] mb-4">الطلبات</h1>
      {!isSeller && (
        <button data-testid="consolidate-banner" onClick={() => navigate("/consolidate")} className="w-full flex items-center gap-3 p-4 mb-4 bg-[#FFE5EA] rounded-2xl border border-[#FF3D5A]/30 text-start">
          <Boxes size={20} className="text-[#FF3D5A]" />
          <div className="flex-1"><p className="font-black text-[#161629]">التجميع الذكي للشحنات</p><p className="text-xs text-[#6B7280]">ادمج طلباتك لنفس المدينة وادفع توصيلاً واحداً</p></div>
        </button>
      )}
      {orders.length === 0 ? (
        <EmptyState icon={Receipt} title="لا توجد طلبات" />
      ) : (
        <div className="space-y-4">
          {orders.map((item) => {
            const st = STATUS[item.status] || { ar: item.status, c: "#6B7280" };
            const escrowHeld = item.escrow_state === "held";
            return (
              <div key={item.order_id} data-testid={`order-card-${item.order_id}`} className="bg-white rounded-2xl border border-[#E8EAF2] p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-3 py-1.5 rounded-lg font-bold text-xs" style={{ backgroundColor: st.c + "22", color: st.c }}>{st.ar}</span>
                  <span className="text-xs text-[#6B7280]">#{item.order_id.slice(-6)}</span>
                </div>
                {escrowHeld && <div className="flex items-center gap-2 p-2.5 mb-3 rounded-xl bg-[#FFF7E0] border border-[#FFB627] text-[#B07700] text-xs font-bold"><ShieldCheck size={16} /> المبلغ محفوظ في الضمان</div>}
                {item.escrow_state === "released" && <div className="flex items-center gap-2 p-2.5 mb-3 rounded-xl bg-[#D9F8E8] border border-[#00D26A] text-[#00D26A] text-xs font-bold"><ShieldCheck size={16} /> تم تحرير المبلغ للبائع</div>}
                {!isSeller && item.delivery_otp && item.self_delivery_confirm_pending && (
                  <OtpBadge code={item.delivery_otp} title="رمز استلام طلبك من البائع" hint="أعطِ هذا الرمز (أو امسح QR) للبائع لتأكيد استلامك" />
                )}
                <div className="flex gap-3">
                  <div className="w-16 h-16 rounded-xl bg-[#F7F8FE] overflow-hidden shrink-0 flex items-center justify-center">{item.product_image ? <img src={item.product_image} alt="" className="w-full h-full object-cover" /> : <Package size={24} className="text-[#9CA3AF]" />}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#161629] line-clamp-2">{item.product_name}</p>
                    <p className="text-xs text-[#6B7280] mt-1">{isSeller ? `المشتري: ${item.buyer_name || ""}` : `البائع: ${item.seller_name || ""}`}</p>
                    <p className="text-xs text-[#6B7280]">الكمية: {item.quantity} • {item.payment_method_name || "—"}</p>
                    {(item.color || item.size) && <p className="text-xs text-[#6B7280]">{item.color ? `لون: ${item.color}` : ""}{item.color && item.size ? " • " : ""}{item.size ? `مقاس: ${item.size}` : ""}</p>}
                    <p className="font-black text-[#FF3D5A] mt-1">{item.total_price} د.ل</p>
                  </div>
                </div>
                {(item.delivery_name || item.delivery_city) && (
                  <div className="flex items-center gap-2 mt-3 p-2.5 bg-[#F7F8FE] rounded-xl text-xs text-[#6B7280]">
                    <MapPin size={14} /> {item.delivery_name ? `${item.delivery_name} • ` : ""}{item.delivery_phone ? `${item.delivery_phone} • ` : ""}{item.delivery_city || ""}
                  </div>
                )}
                {item.buyer_note && <p className="mt-3 p-2.5 bg-[#F0F2FA] rounded-xl text-sm">📝 {item.buyer_note}</p>}
                {item.receipt_base64 && <img src={item.receipt_base64} alt="receipt" className="mt-3 w-full max-h-48 object-contain rounded-xl bg-[#F7F8FE]" />}

                {/* Buyer tracking */}
                {!isSeller && item.assigned_driver_id && item.delivery_status !== "delivered" && (
                  <button onClick={() => navigate(`/track/${item.order_id}`)} className="w-full flex items-center justify-center gap-2 mt-3 py-2.5 rounded-xl bg-[#FF3D5A] text-white font-black text-sm"><MapPin size={16} /> تتبّع السائق</button>
                )}

                {/* Seller actions */}
                {isSeller && item.status === "pending" && (
                  <div className="flex gap-3 mt-3">
                    <button data-testid={`reject-${item.order_id}`} onClick={() => sellerLegacy(item.order_id, "rejected")} className="flex-1 h-11 rounded-xl bg-[#FFE5EA] text-[#FF3D5A] font-black">رفض</button>
                    <button data-testid={`confirm-${item.order_id}`} onClick={() => sellerLegacy(item.order_id, "confirmed")} className="flex-1 h-11 rounded-xl bg-[#00D26A] text-white font-black">تأكيد</button>
                  </div>
                )}
                {isSeller && item.status === "confirmed" && <button data-testid={`ship-${item.order_id}`} onClick={() => markShipped(item.order_id)} className="w-full h-11 rounded-xl bg-[#3742FA] text-white font-black mt-3">📦 تم الشحن</button>}
                {isSeller && item.status === "shipped" && <button data-testid={`delivered-${item.order_id}`} onClick={() => isSelfDelivery(item) ? openSelfDeliver(item) : markDelivered(item.order_id)} className="w-full h-11 rounded-xl bg-[#9333EA] text-white font-black mt-3">🚚 تم التسليم للمشتري</button>}
                {isSeller && item.status === "shipped" && item.self_delivery_confirm_pending && <p className="text-xs text-[#B07700] text-center mt-2">تم إرسال رمز الاستلام للمشتري — أدخله أو امسح QR لتأكيد التسليم</p>}

                {/* Buyer actions */}
                {!isSeller && item.status === "delivered" && escrowHeld && (
                  <div className="flex gap-3 mt-3">
                    <button data-testid={`dispute-${item.order_id}`} onClick={() => openDispute(item)} className="flex-1 h-11 rounded-xl bg-[#FFE5EA] text-[#FF3D5A] font-black flex items-center justify-center gap-1"><AlertCircle size={16} /> فتح نزاع</button>
                    <button data-testid={`confirm-received-${item.order_id}`} onClick={() => confirmReceived(item.order_id)} className="flex-1 h-11 rounded-xl bg-[#00D26A] text-white font-black">✅ تأكيد الاستلام</button>
                  </div>
                )}

                <button data-testid={`chat-order-${item.order_id}`} onClick={() => navigate(`/chat/order/${item.order_id}`)} className="w-full flex items-center justify-center gap-2 mt-3 py-2.5 rounded-xl bg-[#1DA1F2] text-white font-black text-sm"><MessageCircle size={16} /> محادثة الطلب</button>
              </div>
            );
          })}
        </div>
      )}

      {deliverOrder && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="deliver-otp-modal">
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-black text-[#161629]">تأكيد التسليم للمشتري</h3>
              <button data-testid="deliver-otp-close" onClick={() => setDeliverOrder(null)}><X size={22} /></button>
            </div>
            <p className="text-sm text-[#6B7280] text-right mb-3">أدخل رمز الاستلام الظاهر لدى المشتري (4 أرقام) أو امسح رمز QR الخاص به.</p>
            <input data-testid="deliver-otp-input" value={otpInput} onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="0000" className="w-full text-center text-3xl font-black tracking-[0.5em] py-3 rounded-xl border border-[#E8EAF2] bg-[#F7F8FE]" />
            <button data-testid="deliver-scan-qr" onClick={() => setScanOpen(true)} className="w-full flex items-center justify-center gap-2 mt-3 py-2.5 rounded-xl bg-[#F3E8FF] text-[#9333EA] font-black"><QrCode size={18} /> مسح رمز QR بالكاميرا</button>
            <button data-testid="deliver-otp-submit" disabled={otpBusy || otpInput.trim().length < 4} onClick={submitSellerDeliver} className="w-full h-12 rounded-xl bg-[#9333EA] text-white font-black mt-3 disabled:opacity-50">{otpBusy ? "..." : "تأكيد التسليم"}</button>
          </div>
        </div>
      )}
      <QrScanModal open={scanOpen} onClose={() => setScanOpen(false)} onScan={onQrScan} />
    </div>
  );
}
